import { AppLink, Button, Field, Input } from "@nodus/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard, PASSWORD_RULE, passwordProblem } from "../../../app/auth-card";
import { useCaptcha } from "../../../app/turnstile";
import { useAuth } from "../../../auth/auth-context";

export const Route = createFileRoute("/sign-up")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => (typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
  component: SignUp,
});

function SignUp() {
  const { redirect } = Route.useSearch();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const captcha = useCaptcha();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string>();
  const [busy, setBusy] = React.useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await auth.signUp(email.trim(), password, captcha.token);
      if (r.needsVerification) {
        void navigate({ to: "/verify-email", search: { email: email.trim(), ...(redirect ? { redirect } : {}) } });
      } else {
        void navigate({ href: redirect ?? "/" });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-up failed.");
    } finally {
      setBusy(false);
      captcha.reset();
    }
  }

  return (
    <AuthCard
      title="Create your Nodus account"
      description="Run jobs, sandboxes and agents on right-fit compute with prepaid, exact billing."
      footer={
        <>
          Already have an account?{" "}
          <AppLink href="/sign-in" className="font-medium text-fg-primary underline">
            Sign in
          </AppLink>
        </>
      }
    >
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <Field id="email" label="Email" required>
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="password" label="Password" hint={PASSWORD_RULE} error={password ? passwordProblem(password) : undefined} required>
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {captcha.widget}
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        <Button type="submit" disabled={busy || !email || !!passwordProblem(password) || !captcha.ready}>Create account</Button>
        <p className="text-small text-fg-muted">
          By creating an account you agree to the{" "}
          <a href="https://nodus-compute.ai/terms/" target="_blank" rel="noopener" className="underline">
            Terms
          </a>{" "}
          and the{" "}
          <a href="https://nodus-compute.ai/privacy/" target="_blank" rel="noopener" className="underline">
            Privacy policy
          </a>
          .
        </p>
        <p className="text-small text-fg-muted">Have an invite? Open the link in your invite email; it brings you back here and joins you to the organization.</p>
      </form>
    </AuthCard>
  );
}
